import React, { useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import configs from "config";

import { VendorTheme } from "theme";
import {
  createBrowserRouter,
  Navigate,
  redirect,
  RouterProvider,
} from "react-router-dom";
import App from "pages/home";
import Login from "pages/login";
import GlobalCss from "styles/global";
import Main from "components/Main";
import Help from "pages/help";
import { VendorThemeContext } from "contexts/vendorContext";
import * as paths from "./paths";
import MyAccount from "pages/my-account";
import NotFound from "pages/not-found";
import {
  AUTH_USER,
  getItemSessionStorage,
  THEME_PROVIDER,
  User,
} from "utils/helpers";
import { breakpoints } from "styles/utils";

const getAuth = (): User => {
  const user = getItemSessionStorage(
    `${AUTH_USER}_${configs.localStorageKey}`
  ) as User;
  return user;
};

const router = createBrowserRouter([
  {
    path: paths.HOME,
    element: (
      <Main>
        <App />
      </Main>
    ),
  },
  {
    path: paths.LOGIN,
    element: (
      <Main>
        <Login />
      </Main>
    ),
    loader: () => {
      const user = getAuth();
      if (!user.authenticated) {
        return null;
      }
      return redirect(paths.MY_ACCOUNT);
    },
  },
  {
    path: paths.MY_ACCOUNT,
    element: (
      <Main>
        <MyAccount />
      </Main>
    ),
    loader: () => {
      const user = getAuth();
      if (!user.authenticated) {
        return redirect(paths.LOGIN);
      }
      return null;
    },
  },
  {
    path: paths.HELP,
    element: (
      <Main>
        <Help />
      </Main>
    ),
  },
  {
    path: paths.NOT_FOUND,
    element: (
      <Main>
        <NotFound />
      </Main>
    ),
  },
  //if route does not exist send it to home page
  {
    path: "*",
    element: <Navigate replace to="/404"></Navigate>,
  },
]);

const defaultTheme = createTheme({
  typography: {
    htmlFontSize: 10,
    h1: {
      fontSize: "1.6rem",
      fontWeight: 600,
      [`@media (min-width:${breakpoints.medium})`]: {
        fontSize: "2.4rem",
      },
    },
    h2: {
      fontSize: "1.4rem",
      fontWeight: 400,
      [`@media (min-width:${breakpoints.medium})`]: {
        fontSize: "2.0rem",
      },
    },
    h6: {
      fontWeight: 300,
    },
    body2: {
      fontSize: "1.4rem",
      fontWeight: 600,
      [`@media (min-width:${breakpoints.medium})`]: {
        fontSize: "2rem",
      },
    },
    button: {
      fontSize: "1.4rem",
      fontWeight: 400,
      [`@media (min-width:${breakpoints.medium})`]: {
        fontSize: "1.6rem",
      },
    },
    caption: {
      fontSize: "1.2rem",
    },
  },
});

function Router() {
  // Need to customize responsive typography https://mui.com/material-ui/customization/theming/#responsivefontsizes-theme-options-theme
  //https://mui.com/material-ui/customization/typography/

  const [vendorTheme, setVendorTheme] = useState<VendorTheme | undefined>();

  const setDefaultTheme = () => {
    const defaultThemeVendor: VendorTheme = {
      primaryColor: "#F4F4F4",
      secondaryColor: "#FFFFFF",
      logoImage: "images/kaspersky-logo-geral.svg",
      bannerImage: "images/kaspersky-banner.png",
      bannerImageMobile: "images/kaspersky-banner-mobile.png",
      vendorTitleImage: "images/kaspersky-title.svg",
      isDarkTheme: false, // When true text color should be white
      isDefaultTheme: true,
    };

    //Setting Vendor theme that comes from Quero Um Antivírus API
    //Ex: LogoImage, BannerImage, Vendor Name
    setVendorTheme(defaultThemeVendor);
  };

  useEffect(() => {
    const savedTheme = getItemSessionStorage(
      `${THEME_PROVIDER}_${configs.localStorageKey}`
    ) as VendorTheme;

    if (!savedTheme) {
      console.log("savedTheme====>", savedTheme);
      setDefaultTheme();
    }
  }, []);

  return (
    <React.StrictMode>
      <CssBaseline />
      <ThemeProvider theme={defaultTheme}>
        <GlobalCss theme={defaultTheme} />
        <VendorThemeContext.Provider
          value={{
            vendorTheme,
            setVendorTheme,
          }}
        >
          <RouterProvider router={router} />
        </VendorThemeContext.Provider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

export default Router;
